import type { AgentHost, DeviceProperties } from '@runtime/agent.js';

import { collectDeviceProperties, getStorageInfo } from './device-properties.js';
import { isHttpUrl } from './url-safety.js';

/**
 * AgentHost implementation for plain browsers and the Vite dev server.
 *
 * In production-on-device, the SOTI JS Agent API wires to native Android
 * (`launchApp` dispatches an intent, `reboot` calls into MobiControl, etc.).
 * In the browser we have neither — only `openUrl` is meaningful. The other
 * methods throw a structured error which the engine catches and surfaces
 * as `{ ok: false, reason: 'agentError', message }` to the renderer.
 *
 * This is the right behavior: developers see exactly what would have
 * happened on a real device, surfaced via the renderer's normal failure UX.
 */
export class BrowserAgentHost implements AgentHost {
  launchApp(packageName: string): Promise<void> {
    return Promise.reject(new Error(`launchApp(${packageName}) requires the SOTI JS Agent API`));
  }

  openUrl(url: string, options?: { external?: boolean; tab?: 'same' | 'new' }): Promise<void> {
    // The `external` flag is meaningless in a browser — there's no
    // "outside the browser" we can route to. We can, however, honor
    // `tab`: 'new' approximates the on-device "new Chrome tab" by
    // opening a new window/tab, which is the closest preview a plain
    // browser can give. Default ('same' / undefined) navigates in
    // place. Never refuse (contract: hosts no-op what they can't honor).
    if (typeof window === 'undefined') {
      return Promise.reject(new Error('openUrl requires a window'));
    }
    // Defense in depth: never navigate to a javascript:/data:/intent: URL —
    // it would execute in the launcher origin.
    if (!isHttpUrl(url)) {
      return Promise.reject(new Error(`openUrl refused non-http(s) URL: ${url}`));
    }
    if (options?.tab === 'new') {
      window.open(url, '_blank', 'noopener');
    } else {
      window.location.href = url;
    }
    return Promise.resolve();
  }

  reboot(): Promise<void> {
    return Promise.reject(new Error('reboot requires the SOTI JS Agent API'));
  }

  clearAppData(packageName: string | undefined): Promise<void> {
    return Promise.reject(
      new Error(`clearAppData(${packageName ?? '<launcher>'}) requires the SOTI JS Agent API`),
    );
  }

  runScript(scriptRef: string): Promise<void> {
    return Promise.reject(new Error(`runScript(${scriptRef}) requires the SOTI JS Agent API`));
  }

  syncNow(): Promise<void> {
    return Promise.reject(new Error('syncNow requires the SOTI JS Agent API'));
  }

  /**
   * Browser-side `getDeviceProperties` — reports what the WebView itself
   * can measure (UA-derived OS, screen, locale, timezone, online flag,
   * Network Information API estimates, storage quota). MDM-only fields
   * (IMEI, SSID, carrier, signalDbm, vpnConnected) are intentionally
   * omitted; on a real device the production AgentHost fills them in.
   */
  async getDeviceProperties(): Promise<DeviceProperties> {
    const browser = collectDeviceProperties();
    const storage = await getStorageInfo();

    const out: DeviceProperties = {
      formFactor: browser.formFactor,
      os: browser.os,
      browser: browser.browser,
      language: browser.language,
      timezone: browser.timezone,
      online: browser.online,
      screen: browser.screen,
    };
    if (browser.hardwareConcurrency !== undefined) {
      out.hardwareConcurrency = browser.hardwareConcurrency;
    }
    if (browser.deviceMemoryGb !== undefined) out.deviceMemoryGb = browser.deviceMemoryGb;
    if (storage) {
      out.storageUsageBytes = storage.usageBytes;
      out.storageQuotaBytes = storage.quotaBytes;
    }
    if (browser.connection) out.connection = browser.connection;
    return out;
  }
}
